// ABOUTME: Remembers Apple Health workouts already uploaded, and uploads awaiting a calendar link.
// ABOUTME: Persisted, so leaving the import view can't turn a finished upload into a duplicate.
import { STORAGE_KEYS } from "@shared/constants/storageKeys"
import { storageService } from "@shared/services/storageService"

const MAX_IDS = 500

/**
 * In-memory mirror of both records. `storageService` swallows a failed write
 * (quota, storage disabled), and a silently lost record turns a resumable
 * upload into a duplicate — so keep the session's own copy alongside it.
 */
const importedInMemory = new Set<string>()
const pendingInMemory = new Map<string, string>()

export function loadImportedWorkoutIds(): Set<string> {
  const stored = storageService.get<unknown>(STORAGE_KEYS.HEALTH_IMPORTED_WORKOUTS)
  const ids = Array.isArray(stored) ? stored.filter((id) => typeof id === "string") : []

  return new Set([...ids, ...importedInMemory])
}

export function rememberImportedWorkout(workoutId: string): void {
  importedInMemory.add(workoutId)
  // The mirror is capped like the stored list, oldest first — a Set iterates
  // in insertion order.
  for (const oldest of importedInMemory) {
    if (importedInMemory.size <= MAX_IDS) break
    importedInMemory.delete(oldest)
  }

  const ids = loadImportedWorkoutIds()
  ids.add(workoutId)
  // Cap the persisted set so it can't grow without bound.
  storageService.set(STORAGE_KEYS.HEALTH_IMPORTED_WORKOUTS, Array.from(ids).slice(-MAX_IDS))
}

/**
 * Activities already uploaded but not yet linked to the calendar. A retry has
 * to resume the linking rather than upload the file again — and the athlete
 * usually leaves the view before retrying, so an in-memory map won't do.
 */
function loadPendingLinks(): Record<string, string> {
  const stored = storageService.get<unknown>(STORAGE_KEYS.HEALTH_PENDING_ACTIVITY_LINKS)
  if (!stored || typeof stored !== "object" || Array.isArray(stored)) return {}

  return Object.fromEntries(
    Object.entries(stored as Record<string, unknown>).filter(([, activityID]) => typeof activityID === "string"),
  ) as Record<string, string>
}

export function pendingActivityIdFor(workoutId: string): string | undefined {
  return pendingInMemory.get(workoutId) ?? loadPendingLinks()[workoutId]
}

export function rememberPendingActivity(workoutId: string, userActivityID: string): void {
  pendingInMemory.set(workoutId, userActivityID)

  const pending = loadPendingLinks()
  pending[workoutId] = userActivityID
  storageService.set(STORAGE_KEYS.HEALTH_PENDING_ACTIVITY_LINKS, pending)
}

export function forgetPendingActivity(workoutId: string): void {
  pendingInMemory.delete(workoutId)

  const pending = loadPendingLinks()
  if (!(workoutId in pending)) return

  delete pending[workoutId]
  storageService.set(STORAGE_KEYS.HEALTH_PENDING_ACTIVITY_LINKS, pending)
}

/**
 * Drops every record, in storage and in memory. Called on logout: HealthKit
 * ids are per-device, so the next athlete on this phone must not see someone
 * else's workouts as imported — and the in-memory mirror outlives a logout
 * that doesn't reload the page.
 */
export function clearImportedWorkouts(): void {
  importedInMemory.clear()
  pendingInMemory.clear()
  storageService.remove(STORAGE_KEYS.HEALTH_IMPORTED_WORKOUTS)
  storageService.remove(STORAGE_KEYS.HEALTH_PENDING_ACTIVITY_LINKS)
}
